import { useMemo } from "react";
import PageHeader from "@/components/UI/PageHeader";
import { TabsSwitcher } from "@/components/UI/TabsSwitcher";
import { useDatasetPageNames } from "@/hooks/useDatasetPageNames";
import { useDataPageTabSearchParam } from "@/hooks/useDataPageTabSearchParam";
import { MetadataDashboardTemplate } from "./MetadataDashboardTemplate";
import { ALL_TAB_KEY, getPageTabItems } from "./pageTabs";
import type { PageTabConstants, PageTabItem } from "./pageTabs.types";

type TabbedDatasetPageProps = {
  title: string;
  tooltipText: string;
  datasetName: string;
  pageFilterKeys: string[];
  constants: PageTabConstants;
  subjectId?: string;
};

export function TabbedDatasetPage({
  title,
  tooltipText,
  datasetName,
  pageFilterKeys,
  constants,
  subjectId,
}: TabbedDatasetPageProps) {
  const { pageNames, filterKey, isLoading } = useDatasetPageNames(
    datasetName,
    pageFilterKeys,
    subjectId,
  );

  const tabItems: PageTabItem[] = useMemo(
    () =>
      filterKey
        ? getPageTabItems(constants, pageNames, filterKey)
        : [{ key: ALL_TAB_KEY, label: "All", constants }],
    [constants, pageNames, filterKey],
  );

  const { activeKey, setActiveKey } = useDataPageTabSearchParam(
    tabItems.map((tab) => tab.key),
    ALL_TAB_KEY,
  );
  const activeTab =
    tabItems.find((tab) => tab.key === activeKey) ?? tabItems[0];

  return (
    <div className="w-full p-4">
      <div className="flex justify-between mb-4 relative z-20">
        <div className="flex gap-2">
          <PageHeader title={title} tooltipText={tooltipText} />
        </div>
        {!isLoading && tabItems.length > 1 && (
          <TabsSwitcher
            tabItems={tabItems}
            activeTab={activeTab}
            setActiveTab={(tab: PageTabItem) => setActiveKey(tab.key)}
            maxLabelChars={25}
          />
        )}
      </div>
      <MetadataDashboardTemplate
        type={datasetName}
        constants={activeTab.constants}
        subjectId={subjectId}
        dataPageNameField={filterKey}
        pinnedFilters={activeTab.pinnedFilters}
      />
    </div>
  );
}
